import { useV1Insurances } from "@hermes/api";
import { useLocale, useMessage } from "@hermes/intl";
import { availableCountries, resolveCountryInfo } from "@hermes/shared/schemas";
import { type ReactNode, useMemo, useState } from "react";

import { Context, type CountryInfo, type SearchLabel } from "./context";

export const AppConfigContext = ({ children }: { children: ReactNode }) => {
  const message = useMessage();
  const [locale] = useLocale();

  const { data: insurancesData } = useV1Insurances({
    limit: "none",
    locale: locale.baseName,
  });

  const availableCountriesInfo = useMemo(() => {
    const countryIds = availableCountries.codes;

    const infos: CountryInfo[] = countryIds.map((key) => {
      const countryInfo = resolveCountryInfo(key);

      switch (key) {
        case "kz":
          return {
            ...countryInfo,
            name: message({
              id: "dfa4330f-e89b-4ad8-859b-8008a7652c30",
              default: "Kazakhstan",
            }),
            region: "kz",
            lang: "ru-kz",
          };
        case "kg":
          return {
            ...countryInfo,
            name: message({
              id: "cc4c1d9b-36aa-4bc7-a710-c1353038ca44",
              default: "Kyrgyzstan",
            }),
            region: "kg",
            lang: "ru-kg",
          };
        case "hu":
          return {
            ...countryInfo,
            name: message({
              id: "da854328-dd47-41b9-a2e8-55008a49a5e0",
              default: "Hungary",
            }),
            region: "hu",
            lang: "hu-hu",
          };
        case "ro":
          return {
            ...countryInfo,
            name: message({
              id: "bb29373e-7517-4469-ba3d-84af12cd2412",
              default: "Romania",
            }),
            region: "ro",
            lang: "ro-ro",
          };

        case "ae":
          return {
            ...countryInfo,
            name: message({
              id: "common.dubai",
              default: "UAE",
            }),
            region: "ae",
            lang: "en-ae",
          };
      }
    });

    return infos;
  }, [message]);

  const country = useMemo(() => {
    const id = availableCountries.parse(import.meta.env.DOQ_COUNTRY_ID);

    return availableCountriesInfo.find((country) => country.countryId === id)!;
  }, [availableCountriesInfo]);

  const [searchLabel, setSearchLabel] = useState<SearchLabel>();

  return (
    <Context.Provider
      value={{
        isRobot: window.isRobot,
        country,
        availableCountriesInfo,
        searchLabel,
        changeSearchLabel: setSearchLabel,
        socialMediaLinks: {
          facebook: import.meta.env.DOQ_FACEBOOK,
          instagram: import.meta.env.DOQ_INSTAGRAM,
        },
        insurances: insurancesData?.results || [],
      }}
    >
      {children}
    </Context.Provider>
  );
};
